import AutoComplete from '@tarekraafat/autocomplete.js';
import {
    ComponentObject,
    ComponentSelectors,
    ComponentElements,
    ComponentService,
    ComponentInjector,
    ComponentSelector,
    UIComponent,
    Inject,
    BrowserUtils,
    Validator
} from '@atypon/ui-utils';

class Texts extends ComponentObject { }

class ClassList extends ComponentObject {
    autocompleteResultItem = 'autoComplete__item';
    citationSearchJournalField = 'quick-search--citation__journal-search__input';
    citationSearch = 'quick-search--citation';
    hideAutoComplete = 'hide-auto-complete';
}

class Selectors extends ComponentSelectors {
    qsAutocomplete = new ComponentSelector('input.auto-complete');
    qsForms = new ComponentSelector('[name="defaultQuickSearch"],[name="thisJournalQuickSearch"], [name="thisBookQuickSearch"]');
    qsDropdowns = new ComponentSelector('.tab__nav__item__link');
    qsHeaderWrapper = new ComponentSelector('.quick-qsHeaderWrapper', 'document');
    qsSeriesKey = '[name=SeriesKey]';
    qsItems = '.quick-search__search-items';
    citationSearch = '.quick-search-pane__citation';
    autocompleteSelectedResultItem = '.autoComplete__item[aria-selected="true"]';
    citationSearchJournalField = new ComponentSelector('.quick-search--citation__journal-search__input')
    citationSearchDoiField = new ComponentSelector('.quick-search--citation__journal-search__input__doi')
    citationSubmit = new ComponentSelector('.citation-search__submit');
    inputGroup = '.input-group'
    journalSearchHideAutocompleteWrapper = '.quick-search--citation__journal-search'
    autoCompleteWrapper = '.autoComplete_wrapper'
}

class Elements extends ComponentElements<Selectors> {
    qsForms: Array<HTMLElement> = [];
    qsDropdowns: Array<HTMLElement> = [];
    qsAutocomplete: Array<HTMLInputElement> = [];
    qsHeaderWrapper: HTMLElement;
    citationSubmit: HTMLElement;
    citationSearchJournalField: HTMLInputElement;
    citationSearchDoiField: HTMLInputElement;
}


interface QuickSearch extends ComponentService<Selectors, Elements, ClassList, Texts> { }

interface QuickSearchURLParams {
    hs: string;
    pts: string;
    ptgs: string;
    ptfs: string;
    ts: string;
    cs: string;
    pt: string;
}

interface AutoCompleteOptions {
    isSelectOnly: boolean
}

class QuickSearchElm {
    constructor(public el: HTMLInputElement, public searchType: string, public seriesKey: string) { }
}

@ComponentInjector(Selectors, Elements, ClassList, Texts)
class QuickSearch implements UIComponent {
    @Inject()
    protected browserUtils: BrowserUtils;
    @Inject()
    protected validator: Validator;

    activeDropdownJournal: string;
    autcompleteInstances: Map<QuickSearchElm, AutoComplete>;
    _URLParameters: QuickSearchURLParams;
    _autoCompleteOptions: AutoCompleteOptions;

    protected get autoCompleteOptions(): AutoCompleteOptions {
        return this._autoCompleteOptions;
    }

    // On/Off Autocomplete
    protected get isAutocompleteOn(): boolean {
        return true;
    }

    // Submit quick search if user pressed Enter without choosing an autocomplete value
    protected get submitWithoutChoosingValue(): boolean {
        return true;
    }

    // Reserve the user input when changing between different journals
    protected get reserveSearchInputOnJournalChange(): boolean {
        return false;
    }

    protected get activeJournalForm(): HTMLElement {
        return this.domUtils.getElement(`[aria-labelledby="#${this.activeDropdownJournal}"] form`, this.elements.qsHeaderWrapper)
    }

    protected get journalCitationInputs(): NodeListOf<HTMLInputElement>{
        return this.domUtils.closest(this.selectors.inputGroup, this.elements.citationSearchDoiField).querySelectorAll('input')
    }

    protected get URLParameters(): QuickSearchURLParams {
        return {
            hs: 'historyItemsConf',
            pts: 'publicationTitlesConf',
            ptgs: 'groupTitlesConf',
            ptfs: 'publicationItemsConf',
            ts: 'topicsConf',
            cs: 'contributorsConf',
            pt: 'publicationTypesConf'
        }
    }

    constructor(readonly wrapper: HTMLElement) { }

    initialize(): void {
        this._URLParameters = this.URLParameters;
        this.setAutoCompleteOptions();
        this.setActiveJournal();
        this.elements.initialize(this.wrapper);
        if (this.elements.qsAutocomplete.length && this.isAutocompleteOn && !this.browserUtils.isIE) {
            this.initAutocomplete();
        }
        this.addEventListeners();
        this.domUtils.getElements(this.selectors.autoCompleteWrapper).forEach((element, i) => {
            element.setAttribute('aria-label', 'Enter a text or select an option');
        })
    }

    /**
     * Set activeDropdownJournal to default "Anywhere"
     *
     */
    setActiveJournal() {
        this.activeDropdownJournal = this.domUtils.getElement(`${this.selectors.qsItems} > div`)
            ?.getAttribute('aria-labelledby')
            ?.replace(/[#]/, '');
    }

    setAutoCompleteOptions() {
        this._autoCompleteOptions = { isSelectOnly: false };
    }

    protected elementRenderer(item, data) {
        let ele;

        if (this.autoCompleteOptions.isSelectOnly === true) {
            ele = document.createElement('div');
        } else {
            ele = document.createElement('a');
            ele.href = data.value.url;
        }

        ele.className = "autoComplete__link--grid";
        ele.innerHTML = `<span class="autoComplete__text">${data.value.highlight}</span>`;
        // TODO, fix the global var declaration.
        // @ts-ignore
        if (data.value.paramFormatted && config.enableAutoCompleteCategory) {
            ele.innerHTML = `<span class='autoComplete__category'>${data.value.paramFormatted}</span> ${ele.innerHTML}`;
        }
        item.setAttribute('aria-label', ele.innerText);
        item.innerHTML = '';
        item.appendChild(ele);
    }

    protected get defaultConfigs() {
        return {
            threshold: 2,
            debounce: 300,
            resultItem: {
                element: this.elementRenderer.bind(this),
                class: `${this.classList.autocompleteResultItem} my-2`
            },
            searchEngine: (query, record) => {
                return record.highlight || record.label;
            },
        }
    }

    protected getConfigs(quickSearchElm: QuickSearchElm, autoCompleteObj: AutoComplete) {
        return {
            data: {
                src: async () => {
                    const url = this.buildFetchURL(quickSearchElm);
                    if (url) {
                        const data = await fetch(url);
                        return await data.json();
                    } else {
                        return [];
                    }
                },
                cache: false
            },
            selector: () => quickSearchElm.el,
            resultsList: this.resultListConfig(quickSearchElm),
            ...this.defaultConfigs
        }
    }

    protected handleAutoCompleteOptions(ele: HTMLElement) {
        // check if it's a citation field, if yes, then autoCompleteOptions.isSelectOnly should be true
        // meaning clicking on autocomplete items will just copy the text
        this.autoCompleteOptions.isSelectOnly = this.domUtils.containsClasses(ele, this.classList.citationSearchJournalField)
    }

    protected initAutoCompleteInstance(quickSearchElm: QuickSearchElm): AutoComplete {
        this.handleAutoCompleteOptions(quickSearchElm.el);
        let autoCompleteObj: AutoComplete = {};
        const configs = this.getConfigs(quickSearchElm, autoCompleteObj);
        autoCompleteObj = new AutoComplete(configs);
        this.domUtils.addEventListener(quickSearchElm.el, 'selection', this.onSelection.bind(this));
        return autoCompleteObj;
    }


    protected resultListConfig(quickSearchElm: QuickSearchElm) {
        return {
            maxResults: 10,
            render: true,
            idName: 'autoComplete-' + Math.ceil(Math.random() * 10000),
            class: 'autoComplete rlist',
            destination: () => this.resultListDestination(quickSearchElm)
        }
    }

    protected resultListDestination(quickSearchElm: QuickSearchElm) {
        if (this.domUtils.closest(this.selectors.citationSearch, quickSearchElm.el)) {
            return quickSearchElm.el.parentElement;
        }
        return quickSearchElm.el.parentElement.parentElement;
    }


    protected initAutocomplete() {
        this.autcompleteInstances = new Map();
        this.elements.qsAutocomplete.forEach((element, i) => {
            element.setAttribute('type', 'search');
            let quickSearchElm = new QuickSearchElm(element, null, null);

            // check if the search element is in a specific journal or anywhere and update it accordingly
            if (element.parentElement.classList.contains('option-journal')) {
                quickSearchElm.seriesKey = this.domUtils.getElement<HTMLInputElement>(this.selectors.qsSeriesKey, element.parentElement)?.value;
                quickSearchElm.searchType = 'journal';
            }

            this.autcompleteInstances.set(
                quickSearchElm,
                this.initAutoCompleteInstance(quickSearchElm)
            );
        });
    }

    addEventListeners(): void {
        if (this.isAutocompleteOn && !this.browserUtils.isIE) {
            this.elements.qsAutocomplete.forEach(input => {
                input.addEventListener('keydown', this.onKeyDown.bind(this, input));
            });
        }

        this.elements.qsForms.forEach(form => {
            form.addEventListener('submit', this.onFormSubmit.bind(this, form));
        })

        if (this.elements.qsDropdowns && this.elements.qsDropdowns.length > 0) {
            this.elements.qsDropdowns.forEach(item => {
                item.addEventListener('click', this.handleDropdownCollapse.bind(this, item));
            })
        }

        this.registerCitationSearchEvents();
        this.autocompleteAnnouncer();
    }

    protected autocompleteAnnouncer(): void {
        const announcerContainer = document.querySelector('.advanced-search__autocomplete__announcer');
        const autocompleteLists = document.querySelectorAll('[id^="autoComplete_list_"]');
        const mutationCallback = function(mutationsList) {
            mutationsList.forEach(mutation => {
                if(mutation.target.children.length){
                    announcerContainer.innerHTML = `${mutation.target.children.length} results are available, use up and down arrow keys to navigate.`
                } else {
                    announcerContainer.innerHTML = `No results are available.`
                }
            });
        };
        const observer = new MutationObserver(mutationCallback);
        autocompleteLists.forEach(element => {
            observer.observe(element, { childList: true });
        });
    }


    protected onFormSubmit(form: HTMLFormElement, e: Event) {
        const inputValue = this.domUtils.getElement<HTMLInputElement>(this.selectors.qsAutocomplete.selector, form)?.value;
        if (inputValue === '') {
            window.location.href = '/search/advanced';
            e.preventDefault();
        } else {
            form.submit()
        }
    }

    protected onSelection(event: CustomEvent & { target: HTMLInputElement }) {
        if (this.autoCompleteOptions.isSelectOnly === true) {
            // set input field value to the label of the clicked autocomplete item
            event.target.value = event.detail.selection.value.label;
        } else {
            window.location.href = event.detail.selection.value.url;
        }
    }


    protected onKeyDown(input: HTMLInputElement, e: KeyboardEvent,) {
        if (e.keyCode === 13) {
            // this is necessary since the autocomplete plugin prevents default on Enter
            const submitForm = () => this.domUtils.closest('form', input).dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            if (this.submitWithoutChoosingValue) {
                if (this.domUtils.getElement(this.selectors.autocompleteSelectedResultItem, this.wrapper)) {
                    return
                }
                return submitForm()
            } else if (input.value !== '') {
                return
            }
            submitForm()
        }
    }

    protected handleDropdownCollapse(item: HTMLElement, e: Event) {
        const selectedItemId = item.id;
        if (this.activeDropdownJournal === selectedItemId) {
            // prevent collapse default behavior of collapsing the menu when pressing on it again
            item.removeAttribute('data-toggle');
            setTimeout(() => {
                item.setAttribute('data-toggle', 'collapse');
            }, 0);
        } else {
            if (this.reserveSearchInputOnJournalChange) {
                if (this.activeJournalForm) {
                    const input = this.domUtils.getElement<HTMLInputElement>(this.selectors.qsAutocomplete.selector, this.activeJournalForm);
                    this.activeDropdownJournal = selectedItemId;
                    input.value = input.value; // huh?
                }
            } else {
                this.activeDropdownJournal = selectedItemId;
            }
        }
    }

    protected registerCitationSearchEvents() {
        if (!this.domUtils.containsClasses(this.wrapper, this.classList.citationSearch)) {
            return
        }

        this.domUtils.addEventListener(this.elements.qsAutocomplete, 'input', this.changeSubmitButtonState.bind(this));

        this.domUtils.addEventListener(this.elements.citationSearchDoiField, 'input', this.changeSubmitButtonState.bind(this));
        this.domUtils.addEventListener(this.elements.citationSearchDoiField, 'input', this.onCitationSearchDoiField.bind(this));

        this.changeSubmitButtonState(); // initial check to enable submit button if the fields have a valid value from the JSON
    }

    protected onCitationSearchDoiField() {
        let ele = this.elements.citationSearchDoiField,
            hasValue = ele.value !== "";

        ele.setAttribute('name', hasValue ? ele.dataset.name : ""); // remove DOI field name/value from submit payload unless it has a valid value

        this.journalCitationInputs.forEach((input) => {
            if (
                input.getAttribute('name') === "quickLinkDoi" ||
                input.getAttribute('data-name') === "quickLinkDoi" ||
                input.getAttribute('name') === "quickLink"
            ) {
                return;
            }
            if (input.getAttribute('name') === "quickLinkJournal") {
                this.domUtils.toggleClasses(
                    this.domUtils.closest(this.selectors.journalSearchHideAutocompleteWrapper, input),
                    this.classList.hideAutoComplete,
                    hasValue
                ) // this is to hide autocomplete suggestions, as they sometimes render after you've already written something
                // in the DOI field, and you can't hide them unless you **select journal field > click somewhere else to
                // blur it**, and you can't do this as that field is disabled
            }
            input.disabled = hasValue; // disabled all other fields if the DOI one has a valid value
        })
    }

    protected changeSubmitButtonState() {
        let allEmpty = true;

        if (
            this.validator.isNotEmpty(this.elements.citationSearchJournalField) ||
            this.validator.isNotEmpty(this.elements.citationSearchDoiField)
        ) {
            allEmpty = false;
        }

        // enable if at least one main field has a valid value
        if (allEmpty) {
            this.elements.citationSubmit.setAttribute('disabled', 'true');
        } else {
            this.elements.citationSubmit.removeAttribute('disabled');
        }
    }

    protected buildFetchURL(element: QuickSearchElm) {
        const parameters = Object.entries(this.URLParameters).reduce((acc, [key, prop]) => {
            const value = element.el.dataset[prop];
            if (value) {
                acc.push(`${key}=${value}`);
            }
            return acc;
        }, []);
        const term = element.el.value.trim().replace(/\s\s+/g, ' ');
        if (
            term.split(' ').length >= parseInt(element.el.dataset.autoCompleteMaxWords) ||
            term.length >= parseInt(element.el.dataset.autoCompleteMaxChars)
        ) {
            return '';
        }
        // title-auto-complete maybe should be used for citation but it hasn't been used in literatum for quite a while
        return `/action/doSuggest?target=auto-complete&query=${encodeURIComponent(term)}&${parameters.join(
            '&'
        )}&fl=PubID${element.searchType === 'journal' ? '&within=' + element.seriesKey : ''}`;
    }
}

export default QuickSearch;

export {
    Elements as QuickSearchElements,
    Selectors as QuickSearchSelectors,
    ClassList as QuickSearchClassList,
    Texts as QuickSearchTexts,
    AutoCompleteOptions as QuickSearchAutoCompleteOptions,
    QuickSearchElm
}